// src/contexts/ImportFromExcelContext.tsx
// Provides a persistent ImportFromExcel modal that survives route changes.
// Pages call openImport() to launch it; a custom DOM event is dispatched when
// the import finishes so pages can decide whether to refresh.

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { ImportFromExcel } from '@/components/GridView/ImportFromExcel/ImportFromExcel';
import type { DashboardGroupLike } from '@/components/GridView/ImportFromExcel/ImportFromExcel';

// ── Custom event ──────────────────────────────────────────────────────────────

export const IMPORT_EXCEL_DONE_EVENT = 'importExcelDone';

export interface ImportExcelDoneDetail {
  dashboardId: number;
  projectId?: string;
}

// ── Public API ────────────────────────────────────────────────────────────────

export interface ImportFromExcelOpenParams {
  dashboardId: number;
  dashboardGroups: DashboardGroupLike[];
  configValueId: number;
  projectId?: string;
}

interface ImportFromExcelContextValue {
  openImport: (params: ImportFromExcelOpenParams) => void;
}

const ImportFromExcelContext = createContext<ImportFromExcelContextValue>({
  openImport: () => {},
});

export const useImportFromExcel = () => useContext(ImportFromExcelContext);

// ── Provider ──────────────────────────────────────────────────────────────────

interface ModalState extends ImportFromExcelOpenParams {
  open: boolean;
}

const INITIAL_STATE: ModalState = {
  open: false,
  dashboardId: 0,
  dashboardGroups: [],
  configValueId: 1,
};

export const ImportFromExcelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) =>
{
  const [modal, setModal] = useState<ModalState>(INITIAL_STATE);
  // Ref avoids stale-closure in handleImportComplete
  const modalRef = useRef<ModalState>(INITIAL_STATE);

  const openImport = useCallback((params: ImportFromExcelOpenParams) =>
  {
    const next: ModalState = { open: true, ...params };
    modalRef.current = next;
    setModal(next);
  }, []);

  const handleClose = useCallback(() =>
  {
    setModal(prev => ({ ...prev, open: false }));
  }, []);

  const handleImportComplete = useCallback(() =>
  {
    const { dashboardId, projectId } = modalRef.current;
    const event = new CustomEvent<ImportExcelDoneDetail>(IMPORT_EXCEL_DONE_EVENT, {
      detail: { dashboardId, projectId },
    });
    window.dispatchEvent(event);
  }, []);

  return (
    <ImportFromExcelContext.Provider value={{ openImport }}>
      {children}
      <ImportFromExcel
        open={modal.open}
        onClose={handleClose}
        dashboardId={modal.dashboardId}
        dashboardGroups={modal.dashboardGroups}
        configValueId={modal.configValueId}
        projectId={modal.projectId}
        onImportComplete={handleImportComplete}
      />
    </ImportFromExcelContext.Provider>
  );
};
